import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  buildSupportCaseThread,
  isSupportCaseTerminal,
  SUPPORT_CASE_POLL_MS,
  type SupportFormField,
} from "@gaole/shared";
import { api, ApiError } from "../lib/api";
import { markSupportCaseSeen } from "../lib/supportCaseSeen";
import { toast } from "../stores/toast";
import { Badge, PageLoader, statusColor } from "../components/ui";
import { CaseChatThread } from "../components/support/CaseChatThread";
import { CaseChatComposer } from "../components/support/CaseChatComposer";

interface CaseDetail {
  id: string;
  caseNo: string;
  status: string;
  kind: string;
  userId: string | null;
  createdAt: string;
  updatedAt: string;
  data: Record<string, unknown>;
  form: { name: string; fields: unknown } | null;
  comments: {
    id: string;
    authorId: string | null;
    body: string;
    fileKeys: string[];
    createdAt: string;
  }[];
}

export function SupportCaseChat() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [sending, setSending] = useState(false);
  const threadScrollRef = useRef<HTMLDivElement>(null);

  const caseQuery = useQuery({
    queryKey: ["support-case", id],
    enabled: !!id,
    queryFn: () => api<{ case: CaseDetail }>(`/ai/cases/${id}`),
    refetchInterval: (query) => {
      const status = query.state.data?.case.status;
      if (!status || isSupportCaseTerminal(status)) return false;
      return SUPPORT_CASE_POLL_MS;
    },
  });

  const caseRow = caseQuery.data?.case;
  const formFields = useMemo(
    () => parseFormFields(caseRow?.form?.fields) as SupportFormField[],
    [caseRow?.form?.fields],
  );

  const threadItems = useMemo(() => {
    if (!caseRow) return [];
    return buildSupportCaseThread(
      {
        id: caseRow.id,
        userId: caseRow.userId,
        createdAt: caseRow.createdAt,
        data: caseRow.data,
        comments: caseRow.comments.map((c) => ({
          id: c.id,
          authorId: c.authorId,
          body: c.body,
          fileKeys: c.fileKeys ?? [],
          createdAt: c.createdAt,
        })),
      },
      formFields,
    );
  }, [caseRow, formFields]);

  useEffect(() => {
    const el = threadScrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [threadItems]);

  useEffect(() => {
    if (caseRow?.id && caseRow.updatedAt) {
      markSupportCaseSeen(caseRow.id, caseRow.updatedAt);
      void qc.invalidateQueries({ queryKey: ["cases"] });
    }
  }, [caseRow?.id, caseRow?.updatedAt, qc]);

  const commentMut = useMutation({
    mutationFn: (payload: { body: string; fileKeys: string[] }) =>
      api(`/ai/cases/${id}/comments`, { method: "POST", body: payload }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["support-case", id] });
      void qc.invalidateQueries({ queryKey: ["cases"] });
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Could not send"),
  });

  if (caseQuery.isLoading || !id) return <PageLoader />;
  if (caseQuery.isError || !caseRow) {
    return (
      <div className="space-y-3 px-4 pt-4">
        <Link to="/support" className="inline-flex items-center gap-1 text-xs text-white/50">
          <ArrowLeft size={14} /> Back to Support
        </Link>
        <p className="text-sm text-white/60">Case not found.</p>
      </div>
    );
  }

  const closed = isSupportCaseTerminal(caseRow.status);

  async function handleSend(payload: { body: string; fileKeys: string[] }) {
    setSending(true);
    try {
      await commentMut.mutateAsync(payload);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="flex h-[calc(100dvh-3.5rem-var(--mobile-nav-height)-env(safe-area-inset-bottom))] max-h-[calc(100dvh-3.5rem-var(--mobile-nav-height)-env(safe-area-inset-bottom))] min-h-0 flex-col overflow-hidden px-4 pt-4 pb-2 lg:h-[calc(100dvh-4.5rem)] lg:max-h-[calc(100dvh-4.5rem)]">
      <header className="mb-3 flex shrink-0 items-start gap-3 border-b border-white/5 pb-3">
        <button
          type="button"
          onClick={() => navigate("/support")}
          className="mt-0.5 text-white/50 hover:text-white"
          aria-label="Back"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="min-w-0 flex-1">
          <p className="font-display text-sm font-bold">{caseRow.form?.name ?? caseRow.kind}</p>
          <p className="text-[10px] text-white/40">{caseRow.caseNo}</p>
        </div>
        <Badge color={statusColor(caseRow.status)}>{caseRow.status.replace(/_/g, " ")}</Badge>
      </header>

      <div
        ref={threadScrollRef}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
        data-case-chat-scroll
        aria-label="Case messages"
      >
        <CaseChatThread caseId={caseRow.id} items={threadItems} viewerSide="customer" />
      </div>

      <div className="shrink-0 border-t border-white/10 pt-3">
        <CaseChatComposer disabled={closed} busy={sending || commentMut.isPending} onSend={handleSend} />
      </div>
    </div>
  );
}

function parseFormFields(raw: unknown): SupportFormField[] {
  if (!Array.isArray(raw)) return [];
  return raw as SupportFormField[];
}
